import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  BadgeCheck,
  Building2,
  ChevronLeft,
  ChevronRight,
  Fingerprint,
  IdCard,
  Mail,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  UserRound,
} from 'lucide-react';
import { api, dateOnly, dateTime } from '../lib/api';
import { EmptyState, Status } from '../components/UI';

const initials = value => String(value || '?').trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase() || '?';
const titleCase = value => String(value || '').replaceAll('_', ' ').replace(/\b\w/g, character => character.toUpperCase());

export default function CompanyIDHolderRecord() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setNotice('');
    try {
      setData(await api(`/api/admin/company-id/holders/${encodeURIComponent(id || '')}`));
    } catch (error) {
      setData(null);
      setNotice(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const holder = data?.holder || null;
  const credentials = data?.credentials || [];
  const events = data?.events || [];
  const metrics = useMemo(() => ({
    total: credentials.length,
    active: credentials.filter(row => row.status === 'active').length,
    revoked: credentials.filter(row => row.status === 'revoked').length,
    wallets: credentials.filter(row => row.wallet?.issued).length,
  }), [credentials]);

  if (loading) return <section className="cid-platform cid-holder-record-platform">
    <div className="cid-loading"><div className="cid-spinner"/><strong>Loading holder identity</strong><span>Reading permanent Staff ID and credential history…</span></div>
  </section>;

  if (!holder) return <section className="cid-platform cid-holder-record-platform">
    <button className="cid-record-back" onClick={() => navigate('/company-id?tab=holders')}><ChevronLeft size={16}/>Holder Registry</button>
    {notice && <div className="cid-notice error">{notice}</div>}
    <EmptyState title="Holder record unavailable" body="This Company ID holder could not be loaded."/>
  </section>;

  const latest = credentials[0] || null;
  const profilePhoto = latest?.photo_url || null;
  const legalName = latest?.legal_name || holder.display_name;

  return <section className="cid-platform cid-holder-record-platform">
    <div className="cid-record-toolbar">
      <button className="cid-record-back" onClick={() => navigate('/company-id?tab=holders')}><ChevronLeft size={16}/>Holder Registry</button>
      <button className="cid-button cid-button-ghost cid-button-small" onClick={load}><RefreshCw size={14}/>Refresh</button>
    </div>

    {notice && <div className={`cid-notice ${notice.startsWith('Error:') ? 'error' : ''}`}>{notice}</div>}

    <section className="cid-holder-authority-profile">
      <div className="cid-holder-authority-mark"><Fingerprint size={24}/></div>
      <div className="cid-holder-record-avatar">
        {profilePhoto ? <img src={profilePhoto} alt=""/> : <span>{initials(legalName)}</span>}
      </div>
      <div className="cid-holder-authority-identity">
        <span className="cid-record-kicker">PERMANENT COMPANY ID HOLDER</span>
        <h1 className="govuk-heading-xl">{legalName}</h1>
        <p className="govuk-body">{holder.job_title || latest?.internal_title || 'Staff member'}</p>
        <div className="cid-holder-authority-meta">
          <span><Mail size={14}/>{holder.email || 'No email on record'}</span>
          <span><UserRound size={14}/>Staff record {titleCase(holder.status)}</span>
        </div>
      </div>
      <div className="cid-holder-permanent-id">
        <span>PERMANENT STAFF ID</span>
        <strong>{holder.staff_id_number || 'Not assigned'}</strong>
        <small>{holder.staff_id_assigned_at ? `Assigned ${dateOnly(holder.staff_id_assigned_at)}` : 'Assigned on first credential issuance'}</small>
      </div>
    </section>

    <div className="cid-holder-infra-metrics">
      <HolderMetric icon={IdCard} label="Credentials" value={metrics.total}/>
      <HolderMetric icon={BadgeCheck} label="Active" value={metrics.active}/>
      <HolderMetric icon={Smartphone} label="Wallet passes" value={metrics.wallets}/>
      <HolderMetric icon={ShieldCheck} label="Revoked history" value={metrics.revoked}/>
    </div>

    <section className="cid-panel">
      <div className="cid-panel-heading">
        <div><span className="cid-panel-kicker">CREDENTIAL CHAIN</span><h3 className="govuk-heading-s">Issued credentials</h3><p className="govuk-body">The holder identity is permanent. Individual credentials below have their own lifecycle and can be replaced without changing the Staff ID.</p></div>
      </div>
      {credentials.length ? <div className="cid-holder-credential-chain">{credentials.map((row, index) =>
        <button key={row.id} className="cid-holder-credential-entry" onClick={() => navigate(`/company-id/record/${encodeURIComponent(row.id)}`)}>
          <span className="cid-holder-chain-index">{String(index + 1).padStart(2, '0')}</span>
          <span className="cid-holder-chain-main">
            <strong>{row.credential_number}</strong>
            <small>{row.company_name} · {row.credential_label || 'Company credential'}</small>
            <span>Issued {dateOnly(row.issued_at)}{row.expires_at ? ` · valid until ${dateOnly(row.expires_at)}` : ' · until revoked'}</span>
          </span>
          <span className="cid-holder-chain-company"><Building2 size={14}/>{row.company_number}</span>
          <Status value={row.status}/>
          <span className={`cid-holder-chain-wallet ${row.wallet?.issued ? 'ready' : ''}`}><Smartphone size={13}/>{row.wallet?.issued ? 'Wallet' : 'No wallet'}</span>
          <ChevronRight size={17}/>
        </button>
      )}</div> : <EmptyState title="No credentials issued" body="This staff record does not yet have a Company ID credential."/>}
    </section>

    <section className="cid-panel">
      <div className="cid-panel-heading compact"><div><span className="cid-panel-kicker">HOLDER AUDIT</span><h3 className="govuk-heading-s">Identity and credential activity</h3></div></div>
      {events.length ? <div className="cid-event-list">{events.map(event =>
        <div key={event.id} className="cid-event-row">
          <span className="cid-event-dot"/>
          <div>
            <strong>{event.summary || titleCase(event.event_type)}</strong>
            <small>{dateTime(event.occurred_at)}{event.credential_number ? ` · ${event.credential_number}` : ''}{event.actor_name ? ` · ${event.actor_name}` : ''}</small>
          </div>
        </div>
      )}</div> : <div className="cid-inline-empty"><Activity size={19}/><span>No holder activity has been recorded.</span></div>}
    </section>
  </section>;
}

function HolderMetric({ icon: Icon, label, value }) {
  return <div className="cid-holder-infra-metric"><span><Icon size={17}/></span><div><strong>{value}</strong><small>{label}</small></div></div>;
}
